import { defineStore } from 'pinia';
import { roles } from '@/constants';
import { resolveAccessState } from '@/utils/auth/accessState';

export const useAppStore = defineStore('app', {
    state: () => ({
        mode: '',
        flags: {},
        breadcrumb: [],
        navigationHistory: [], // Tracks visited routes for history-based breadcrumbs
        error: null, // Fatal error for Error.vue page
        bannerError: null, // Non-fatal error for ErrorBanner component
        // Means only "finished onboarding". Fails closed; read `accessState` to route on it.
        active: false,
        // Set when a previously onboarded user is deactivated for a missing agreement; deactivation wipes the
        // BE signal (isActive), so it is captured here first.
        returningToOnboarding: false,
        spotlight: {},
        queue: {},
    }),

    getters: {
        /**
         * Get current app mode
         * @returns {string} Current application mode
         */
        appMode: (state) => state.mode,

        /**
         * Get current role
         */
        isEducator: (state) => state.mode === roles.EDUCATOR_ROLE,
        isVolunteer: (state) => state.mode === roles.EMPLOYEE_ROLE,
        isOrgAdmin: (state) => state.mode === roles.ORGADMIN_ROLE,
        isDeAdmin: (state) => state.mode === roles.DE_ROLE,
        isStudent: (state) => state.mode === roles.STUDENT_ROLE,
        isGroupAdmin: (state) => state.mode === roles.GROUPADMIN_ROLE,
        appModeCapitalised: (state) => {
            const role = state.mode;
            if (!role || typeof role !== 'string') return null;
            return role[0].toUpperCase() + role.substring(1);
        },

        /**
         * Where the visitor stands: anonymous, blocked, onboarding or ready. Derived, so it cannot drift from
         * its inputs.
         * @returns {string} One of ACCESS_STATES
         */
        accessState: (state) => resolveAccessState({ mode: state.mode, error: state.error, onboarded: state.active }),

        /**
         * Get current app error (fatal errors for Error.vue)
         * @returns {object|null} Current error object or null
         */
        appError: (state) => state.error,

        /**
         * Get current banner error (non-fatal errors for ErrorBanner)
         * @returns {object|null} Current banner error object or null
         */
        appBannerError: (state) => state.bannerError,
    },

    actions: {
        /**
         * Update app state properties
         * @param {object} data - Object containing properties to update
         */
        updateApp(data) {
            Object.keys(data).forEach((key) => {
                this[key] = data[key];
            });
        },

        /**
         * Update error state (fatal errors for Error.vue page)
         * @param {object|null} data - Error object or null to clear error
         */
        updateError(data) {
            this.error = data;
        },

        /**
         * Set banner error state (non-fatal errors for ErrorBanner)
         * @param {string} data.title - Error title
         * @param {string} data.message - Error message
         * @param {string} [data.color] - Optional color (defaults to 'error')
         * @param {string} [data.actionTitle] - Optional title for a CTA
         * @param {Function} [data.onDismiss] - Optional callback invoked when the banner is dismissed
         * @param {Function} [data.actionCallback] - Optional CTA callback
         * @param {string} [data.bannerType] - Optional way to describe your error if you want to identify certain types of errors. E.g. 'profile-check' can help identify errors from that system, so your 'api-error' error does not step over and clear that error. Defaults to 'generic' so any set banner is distinguishable from there being no banner at all.
         */
        setBannerError(data) {
            this.bannerError = {
                title: data.title,
                message: data.message,
                color: data.color || 'error',
                onDismiss: data.onDismiss,
                actionTitle: data.actionTitle,
                actionCallback: data.actionCallback,
                bannerType: data.bannerType || 'generic',
            };
        },

        /**
         * Clear banner error state, invoking onDismiss if one was set
         */
        clearBannerError() {
            this.bannerError?.onDismiss?.();
            this.bannerError = null;
        },

        /**
         * Update application mode
         * @param {string} data - New application mode
         */
        updateMode(data) {
            this.mode = data;
        },

        /**
         * Reset breadcrumbs to empty array
         */
        resetBreadcrumbs() {
            this.breadcrumb = [];
        },

        /**
         * Set breadcrumbs directly (used by router guard)
         * @param {Array} breadcrumbs - Array of breadcrumb objects
         */
        setBreadcrumbs(breadcrumbs) {
            this.breadcrumb = breadcrumbs;
        },

        /**
         * Update the last breadcrumb's text (for dynamic content)
         * Use this when a page needs to show dynamic content in the breadcrumb
         * (e.g., organization name, user name, etc.)
         *
         * @param {string} text - New text for the last breadcrumb
         */
        updateLastBreadcrumb(text) {
            if (this.breadcrumb.length > 0) {
                const lastIndex = this.breadcrumb.length - 1;
                this.breadcrumb[lastIndex] = {
                    ...this.breadcrumb[lastIndex],
                    text,
                };
            }
        },

        /**
         * Add a route to navigation history
         * Used to track which routes the user has actually visited
         *
         * @param {Object} routeData - Object with routeName and fullPath
         */
        addToNavigationHistory(routeData) {
            const lastEntry = this.navigationHistory[this.navigationHistory.length - 1];

            // Don't add duplicates if the last entry is the same route
            if (lastEntry && lastEntry.routeName === routeData.routeName) {
                return;
            }

            // Add route to history
            this.navigationHistory.push({
                routeName: routeData.routeName,
                fullPath: routeData.fullPath,
            });
        },

        /**
         * Clear navigation history
         * Called when user visits dashboard or logs out
         */
        clearNavigationHistory() {
            this.navigationHistory = [];
        },

        /**
         * Update feature flags
         * @param {object} params - Parameters object
         * @param {object} params.flags - Object containing feature flags to update
         */
        updateFeatureFlags({ flags }) {
            this.flags = { ...this.flags, ...flags };
        },

        /**
         * Add value to queue
         * @param {object} params - Parameters object
         * @param {*} params.value - Value to add to queue
         * @param {string} params.key - Queue key identifier
         */
        enqueue({ value, key }) {
            // Initialize queue array if it doesn't exist
            if (!this.queue[key]) {
                this.queue[key] = [];
            }

            // Bail if value already in queue
            if (this.queue[key].includes(value)) return;

            // Push value to queue array
            this.queue[key].push(value);
        },

        /**
         * Remove value from queue
         * @param {object} params - Parameters object
         * @param {*} params.value - Value to remove from queue
         * @param {string} params.key - Queue key identifier
         */
        dequeue({ value, key }) {
            // Find index of value in queue
            const index = this.queue[key]?.indexOf(value);

            // Splice to remove it
            if (index !== undefined && index !== -1) {
                this.queue[key].splice(index, 1);
            }
        },
    },
});
